import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FiHome, FiChevronRight, FiUsers, FiTrendingUp, FiArrowRight, FiX, FiInfo } from 'react-icons/fi';
import FamilyBusinessesPage from './FamilyBusinessesPage';
import ManageFoldersTab from './ManageFoldersTab';
import GLChartOfAccountsTab from './GLChartOfAccountsTab';
import UploadDocsPage from './UploadDocsPage';
import ProcessReceiptsTab from './ProcessReceiptsTab';
import JournalEntriesTab from './JournalEntriesTab';
import GLReportsTab from './GLReportsTab';
import FinancialStatementsTab from './FinancialStatementsTab';
import ClientAccountsSubscribe from './ClientAccountsSubscribe';
import { confirmDiscardChanges, setFormDirty } from '../../../lib/unsavedChanges';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import { isClientAccountsSubscribed } from '../../../lib/entitlements';
import './BookkeepingPage.css';

export type BkScope = 'self' | 'clients';

// =============================================
// Menu structure — Masters, Transactions, Reports, Utilities
// =============================================
interface SubMenuItem {
  label: string;
  path: string;
}

interface TopMenuItem {
  label: string;
  key: string;
  items: SubMenuItem[];
}

const BASE_MENU: TopMenuItem[] = [
  {
    label: 'Masters',
    key: 'masters',
    items: [
      { label: 'Family & Businesses',       path: 'family-businesses' },
      { label: 'Local Settings',             path: 'local-settings' },
      { label: 'GL Chart of Accounts Setup', path: 'gl-chart-of-accounts' },
      { label: 'Taxes',                      path: 'taxes' },
      { label: 'Payroll Setup',              path: 'payroll-setup' },
    ],
  },
  {
    label: 'Transactions',
    key: 'transactions',
    items: [
      { label: 'Process Docs / Receipts',  path: 'process-receipts' },
      { label: 'Make Journal Entries',     path: 'bookkeeping-without-scans' },
      { label: 'Edit',                     path: 'edit' },
      { label: 'Delete',                   path: 'delete' },
    ],
  },
  {
    label: 'Reports',
    key: 'reports',
    items: [
      { label: 'Trial Balance & GL',    path: 'trial-balance-gl' },
      { label: 'Expense Reports',       path: 'expense-reports' },
      { label: 'Financial Statements',  path: 'financial-statements' },
      { label: 'Tax Reports',           path: 'tax-reports' },
      { label: 'Receipts Hub',          path: 'receipts-hub' },
    ],
  },
  {
    label: 'Utilities',
    key: 'utilities',
    items: [], // TBD — will be decided later
  },
];

// Build the menu for a given scope. Client's Accounts renames the roster tab to
// "Manage Clients" and adds "Manage Folders" under Masters (clients-only — Your
// Accounts keeps document folders under Documents/DMS).
function buildMenu(scope: BkScope): TopMenuItem[] {
  if (scope !== 'clients') return BASE_MENU;
  return BASE_MENU.map(m => {
    if (m.key !== 'masters') return m;
    const items = m.items.map(i =>
      i.path === 'family-businesses' ? { ...i, label: 'Manage Clients' } : i
    );
    const fbIdx = items.findIndex(i => i.path === 'family-businesses');
    items.splice(fbIdx + 1, 0, { label: 'Manage Folders', path: 'manage-folders' });
    return { ...m, items };
  });
}

// =============================================
// Placeholder for sub-pages
// =============================================
const SubPagePlaceholder: React.FC<{ title: string; parentMenu: string }> = ({ title, parentMenu }) => (
  <div className="bk-subpage-placeholder">
    <div className="bk-subpage-icon">🚧</div>
    <h2>{title}</h2>
    <p>{parentMenu} → {title}</p>
    <span className="bk-subpage-badge">Coming Soon</span>
  </div>
);

// =============================================
// Landing content (no sub-page selected)
// =============================================
const BookkeepingLanding: React.FC<{ onMenuClick: (menuKey: string, fromLanding?: boolean) => void }> = ({ onMenuClick }) => (
  <div className="bk-landing">
    <div className="bk-landing-grid">
      <div className="bk-landing-card" onClick={() => onMenuClick('masters', true)}>
        <div className="bk-landing-card-icon masters">📋</div>
        <h3>Masters</h3>
        <p>Set up family, businesses, taxes, chart of accounts</p>
      </div>
      <div className="bk-landing-card" onClick={() => onMenuClick('transactions', true)}>
        <div className="bk-landing-card-icon transactions">📄</div>
        <h3>Transactions</h3>
        <p>Upload documents, process receipts, edit entries</p>
      </div>
      <div className="bk-landing-card" onClick={() => onMenuClick('reports', true)}>
        <div className="bk-landing-card-icon reports">📊</div>
        <h3>Reports</h3>
        <p>Expense reports, financial statements, tax reports</p>
      </div>
      <div className="bk-landing-card" onClick={() => onMenuClick('utilities', true)}>
        <div className="bk-landing-card-icon utilities">⚙️</div>
        <h3>Utilities</h3>
        <p>Tools and utilities — coming soon</p>
      </div>
    </div>
  </div>
);

// =============================================
// Main Bookkeeping Page
// =============================================
interface BookkeepingPageProps {
  isNewUser?: boolean;  // true = new signup, show About Yourself form, fade rest
  scope?: BkScope;      // 'self' = Your Accounts, 'clients' = Client's Accounts
}

interface ClientLite {
  user_id: number;
  name: string;
  city?: string;
  state?: string;
}

// Resolve the account owner row id (owner_id is null/empty).
function getAccountOwnerId(): number | null {
  const users = getTableData('users');
  // eslint-disable-next-line eqeqeq
  const owner = users.find((u: any) => u.owner_id == null);
  return owner ? Number(owner.user_id) : null;
}

// Resolve the account owner's personal clients (same rule as the GL clients tab)
function loadPersonalClients(): ClientLite[] {
  const users = getTableData('users');
  // eslint-disable-next-line eqeqeq
  const owner = users.find((u: any) => u.owner_id == null);
  if (!owner) return [];
  const ownerId = Number(owner.user_id);
  return users
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == ownerId && u.is_deleted != 1 && u.relationship === 'Personal Client')
    .map((u: any) => ({
      user_id: Number(u.user_id),
      name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Client',
      city: u.address_city || '',
      state: u.address_state || '',
    }));
}

// Shown under Client's Accounts when the owner has no clients yet —
// pitches turning their account into a bookkeeping business (revenue driver).
const StartBkBusinessLanding: React.FC<{ onAddClient: () => void }> = ({ onAddClient }) => (
  <div className="bk-biz-landing">
    <div className="bk-biz-hero">
      <div className="bk-biz-icon"><FiTrendingUp size={40} /></div>
      <h2>Turn Akountant into your bookkeeping business</h2>
      <p>
        You already keep books for yourself and your family. Do it for others and get paid.
        Add clients, manage each one's books separately, and bill per client for bookkeeping,
        payroll, schedules, and tax reports.
      </p>
    </div>
    <div className="bk-biz-points">
      <div className="bk-biz-point"><strong>Separate, secure books</strong><span>Each client's accounts stay isolated from yours and from each other.</span></div>
      <div className="bk-biz-point"><strong>Per-client packages</strong><span>Charge for bookkeeping, payroll, schedules, and tax reports — per client.</span></div>
      <div className="bk-biz-point"><strong>One dashboard</strong><span>Switch between clients without losing context or re-logging in.</span></div>
    </div>
    <div className="bk-biz-cta">
      <button className="bk-biz-cta-btn" onClick={onAddClient}>
        Add your first client <FiArrowRight size={16} />
      </button>
      <span className="bk-biz-cta-note">Clients are managed under Masters → Family &amp; Businesses → Your clients.</span>
    </div>
  </div>
);

// Client picker shown under Client's Accounts once the owner has clients.
const ClientPicker: React.FC<{ clients: ClientLite[]; onPick: (id: number) => void; onAddNew: () => void }> = ({ clients, onPick, onAddNew }) => (
  <div className="bk-client-picker">
    <div className="bk-client-picker-head">
      <FiUsers size={20} />
      <h3>Select a client</h3>
      <p>Choose a client to manage their books. Each client's accounts are kept separate.</p>
    </div>
    <div className="bk-client-picker-list">
      {clients.map(c => (
        <button key={c.user_id} className="bk-client-card" onClick={() => onPick(c.user_id)}>
          <span className="bk-client-avatar">{c.name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}</span>
          <span className="bk-client-info">
            <span className="bk-client-name">{c.name}</span>
            {c.city && <span className="bk-client-loc">{c.city}{c.state ? `, ${c.state}` : ''}</span>}
          </span>
          <FiArrowRight size={16} />
        </button>
      ))}
    </div>
    <button className="bk-client-add-link" onClick={onAddNew}>+ Add or manage clients</button>
  </div>
);

function resolvePageFromHash(menu: TopMenuItem[]): { menu: string; item: SubMenuItem } | null {
  // Check hash first (primary), then fall back to pathname segment
  const hash = window.location.hash.replace('#', '');
  const slug = hash || window.location.pathname.split('/').pop() || '';
  if (!slug) return null;
  for (const m of menu) {
    const item = m.items.find(i => i.path === slug);
    if (item) return { menu: m.label, item };
  }
  return null;
}

const BookkeepingPage: React.FC<BookkeepingPageProps> = ({ isNewUser = false, scope = 'self' }) => {
  const pillarLabel = scope === 'clients' ? "Client's Accounts" : 'Your Accounts';
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Bumped when a sub-tab is re-clicked → remounts the sub-page to reset it.
  const [subResetKey, setSubResetKey] = useState(0);

  // Menu is scope-aware (Client's Accounts adds "Manage Folders" under Masters).
  const menu = useMemo(() => buildMenu(scope), [scope]);

  // Subscription gate (Client's Accounts only): the pillar is locked behind an
  // owner-level subscription. Platform/franchisee actors are auto-subscribed.
  const [subscribed, setSubscribed] = useState<boolean>(
    () => scope !== 'clients' || isClientAccountsSubscribed(getAccountOwnerId() ?? -1)
  );
  const lockedForSubscription = scope === 'clients' && !subscribed;

  // Client's Accounts gate: which clients exist + which one is being managed
  const [clients, setClients] = useState<ClientLite[]>(() => loadPersonalClients());
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  useEffect(() => {
    if (scope !== 'clients') return;
    Promise.all([loadTable('users'), loadTable('user_client_subscription')])
      .then(() => {
        setClients(loadPersonalClients());
        setSubscribed(isClientAccountsSubscribed(getAccountOwnerId() ?? -1));
      })
      .catch(() => {});
  }, [scope]);

  // After a successful (mock) subscribe, re-check and unlock the pillar.
  const handleSubscribed = useCallback(() => {
    setSubscribed(isClientAccountsSubscribed(getAccountOwnerId() ?? -1));
  }, []);

  const selectedClient = useMemo(
    () => clients.find(c => c.user_id === selectedClientId) || null,
    [clients, selectedClientId],
  );
  const [activePage, setActivePage] = useState<{ menu: string; item: SubMenuItem } | null>(
    isNewUser ? { menu: 'Masters', item: menu[0].items[0] } : resolvePageFromHash(menu)
  );
  const menuBarRef = useRef<HTMLDivElement>(null);

  // Client roster lives under "Family & Businesses" — reachable without a
  // pre-selected client so the user can add their first one.
  const pathAllowedWithoutClient = (path?: string) => path === 'family-businesses';
  // Under Client's Accounts, hold a sub-page's content until a client is chosen
  // (except the roster). With no active page, the gate stands in for the landing.
  const showClientGate =
    scope === 'clients' &&
    selectedClientId == null &&
    (!activePage || !pathAllowedWithoutClient(activePage.item.path));

  // Jump to Masters → Family & Businesses (the client roster) within this pillar.
  // `openAddForm` asks the roster to auto-open the "Add a Personal Client" form
  // (used by the "Add your first client" CTA).
  const goToRoster = useCallback(async (openAddForm = false) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    if (openAddForm) {
      try { sessionStorage.setItem('akt_open_add_personal_client', '1'); } catch {}
    }
    const masters = menu.find(m => m.key === 'masters');
    const fb = masters?.items.find(i => i.path === 'family-businesses');
    if (masters && fb) setActivePage({ menu: masters.label, item: fb });
  }, [menu]);

  // Subscribed owners with existing clients land on the roster (image 1) instead
  // of an empty landing. The roster path is reachable without a selected client.
  useEffect(() => {
    if (scope !== 'clients' || !subscribed) return;
    if (activePage || selectedClientId != null || clients.length === 0) return;
    const masters = menu.find(m => m.key === 'masters');
    const fb = masters?.items.find(i => i.path === 'family-businesses');
    if (masters && fb) setActivePage({ menu: masters.label, item: fb });
  }, [scope, subscribed, activePage, selectedClientId, clients.length, menu]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuBarRef.current && !menuBarRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sync hash with active page
  useEffect(() => {
    const newHash = activePage ? '#' + activePage.item.path : '';
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    }
  }, [activePage]);

  // Handle browser back/forward
  useEffect(() => {
    const onHashChange = () => {
      setActivePage(resolvePageFromHash(menu));
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [menu]);

  const handleMenuClick = useCallback(async (menuKey: string, fromLanding = false) => {
    if (!fromLanding && !(await confirmDiscardChanges())) return;
    const m = menu.find(x => x.key === menuKey);
    setOpenMenu(null);
    if (m && m.items.length > 0) {
      setFormDirty(false);
      setActivePage({ menu: m.label, item: m.items[0] });
    }
  }, [menu]);

  const handleSubItemClick = useCallback(async (menu: TopMenuItem, item: SubMenuItem) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    // Re-clicking the sub-tab you're already on resets that sub-page to its landing
    // (remounts it via the key below). confirmDiscardChanges() above warns if dirty.
    if (activePage && activePage.item.path === item.path) {
      setSubResetKey(k => k + 1);
      // For a client-specific sub-tab (GL / Process Docs / Manage Folders), the
      // landing is the client picker — so drop the selected client too.
      if (scope === 'clients' && selectedClientId != null && !pathAllowedWithoutClient(item.path)) {
        setSelectedClientId(null);
      }
    }
    setActivePage({ menu: menu.label, item });
    setOpenMenu(null);
  }, [activePage, scope, selectedClientId]);

  // Find the parent menu object for the active page (used for sub-nav)
  const activeMenuObj = activePage
    ? menu.find(m => m.label === activePage.menu) ?? null
    : null;

  // Render the correct sub-page component based on active selection
  const renderSubPage = () => {
    if (!activePage) return null;
    // Route to real components as they are built
    const viewMode = scope === 'clients' ? 'clients' : 'owner';
    switch (activePage.item.path) {
      case 'family-businesses':
        return <FamilyBusinessesPage scope={scope} />;
      case 'manage-folders':
        return <ManageFoldersTab viewMode={viewMode} selectedClientId={selectedClientId} />;
      case 'gl-chart-of-accounts':
        return <GLChartOfAccountsTab scope={scope} selectedClientId={selectedClientId} />;
      case 'upload-docs':
        return <UploadDocsPage />;
      case 'process-receipts':
        return <ProcessReceiptsTab viewMode={viewMode} selectedClientId={selectedClientId} />;
      case 'bookkeeping-without-scans':
        return <JournalEntriesTab viewMode={viewMode} selectedClientId={selectedClientId} />;
      case 'trial-balance-gl':
        return <GLReportsTab viewMode={viewMode} selectedClientId={selectedClientId} />;
      case 'financial-statements':
        return <FinancialStatementsTab viewMode={viewMode} selectedClientId={selectedClientId} />;
      default:
        return (
          <SubPagePlaceholder
            title={activePage.item.label}
            parentMenu={activePage.menu}
          />
        );
    }
  };

  return (
    <div className={`bk-page ${isNewUser ? 'bk-new-user-mode' : ''}`}>
      {/* New user banner */}
      {isNewUser && (
        <div className="bk-new-user-banner">
          <strong>Welcome!</strong> Please fill in your details below to complete your account setup.
        </div>
      )}

      {/* Managing-client bar (Client's Accounts, once a client is chosen) */}
      {scope === 'clients' && selectedClient && (
        <div className="bk-managing-bar">
          <span>Managing books for <strong>{selectedClient.name}</strong></span>
          <button className="bk-managing-change" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); setActivePage(null); setSelectedClientId(null); }}>
            <FiX size={13} /> Change client
          </button>
        </div>
      )}

      {/* Breadcrumbs — positioned at top, just under page header */}
      {!lockedForSubscription && (activePage || showClientGate) && (
        <nav className="bk-breadcrumbs" aria-label="Breadcrumb">
          <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); window.location.href = '/dashboard'; }}>
            <FiHome size={13} /> Home
          </span>
          <FiChevronRight size={12} className="bk-breadcrumb-sep" />
          <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); setActivePage(null); }}>
            {pillarLabel}
          </span>
          {scope === 'clients' && selectedClient && (
            <>
              <FiChevronRight size={12} className="bk-breadcrumb-sep" />
              <span className="bk-breadcrumb-item bk-breadcrumb-link" onClick={async () => { if (!(await confirmDiscardChanges())) return; setFormDirty(false); setActivePage(null); setSelectedClientId(null); }}>
                {selectedClient.name}
              </span>
            </>
          )}
          {activePage && !showClientGate && (
            <>
              <FiChevronRight size={12} className="bk-breadcrumb-sep" />
              <span
                className="bk-breadcrumb-item bk-breadcrumb-link"
                onClick={() => activeMenuObj && handleMenuClick(activeMenuObj.key)}
              >
                {activePage.menu}
              </span>
              <FiChevronRight size={12} className="bk-breadcrumb-sep" />
              <span className="bk-breadcrumb-item bk-breadcrumb-current">
                {activePage.item.label}
              </span>
            </>
          )}
        </nav>
      )}

      {/* Top Menu Bar — disabled while the Client's Accounts subscription is locked */}
      <div className={`bk-menu-bar ${isNewUser ? 'bk-faded' : ''} ${lockedForSubscription ? 'bk-locked' : ''}`} ref={menuBarRef}>
        {menu.map(m => (
          <div
            key={m.key}
            className="bk-menu-item-wrapper"
            onMouseEnter={() => { if (!lockedForSubscription) setOpenMenu(m.key); }}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button
              className={`bk-menu-btn ${openMenu === m.key ? 'active' : ''} ${activePage?.menu === m.label ? 'current' : ''}`}
              onClick={() => { if (!lockedForSubscription) handleMenuClick(m.key); }}
              disabled={lockedForSubscription}
              aria-disabled={lockedForSubscription}
              title={lockedForSubscription ? 'Subscribe to a package to unlock Client’s Accounts' : undefined}
            >
              {m.label}
            </button>

            {/* Dropdown */}
            {!lockedForSubscription && openMenu === m.key && m.items.length > 0 && (
              <div className="bk-dropdown">
                {m.items.map(item => (
                  <button
                    key={item.path}
                    className={`bk-dropdown-item ${activePage?.item.path === item.path ? 'active' : ''}`}
                    onClick={() => handleSubItemClick(m, item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

            {/* Empty state for Utilities */}
            {!lockedForSubscription && openMenu === m.key && m.items.length === 0 && (
              <div className="bk-dropdown bk-dropdown-empty">
                <span>Coming soon</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Secondary Sub-Nav — shows sibling items within the active menu section */}
      {!lockedForSubscription && activePage && !showClientGate && activeMenuObj && activeMenuObj.items.length > 1 && (
        <div className={`bk-sub-nav-bar ${isNewUser ? 'bk-faded' : ''}`}>
          <div className="bk-sub-nav">
            {activeMenuObj.items.map(item => (
              <button
                key={item.path}
                className={`bk-sub-nav-item ${activePage.item.path === item.path ? 'active' : ''}`}
                onClick={() => handleSubItemClick(activeMenuObj, item)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {/* Manage Clients help — the old info banner, now an ⓘ at the end of the menu
              (outside the scrolling sub-nav so the popover isn't clipped). */}
          {scope === 'clients' && activePage.item.path === 'family-businesses' && (
            <span className="bk-subnav-info" tabIndex={0} aria-label="About managing clients">
              <FiInfo size={15} />
              <span className="bk-subnav-info-pop" role="tooltip">
                <strong>Manage your clients here.</strong> If you are a bookkeeper, accountant, or tax professional,
                add your clients to manage their documents, books, and filings.
                <br /><br />
                <strong>Ownership:</strong> Client data is linked to your account. If a client moves to another
                professional, ownership can be transferred — they get full control of their paperwork under the new accountant.
              </span>
            </span>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className="bk-content">
        {lockedForSubscription ? (
          <ClientAccountsSubscribe onSubscribed={handleSubscribed} />
        ) : showClientGate ? (
          clients.length === 0
            ? <StartBkBusinessLanding onAddClient={() => goToRoster(true)} />
            : <ClientPicker clients={clients} onPick={(id) => setSelectedClientId(id)} onAddNew={() => goToRoster(false)} />
        ) : activePage ? (
          // Keyed by path + reset counter so re-clicking the active sub-tab remounts it.
          <React.Fragment key={`${activePage.item.path}-${subResetKey}`}>
            {renderSubPage()}
          </React.Fragment>
        ) : (
          <BookkeepingLanding onMenuClick={handleMenuClick} />
        )}
      </div>
    </div>
  );
};

export default BookkeepingPage;
